/**
 * LeadQueueList Component
 * 
 * Virtualized list for displaying hot leads with efficient rendering.
 * Uses react-window for performance with large datasets.
 * Falls back to simple list if react-window is not available.
 */

import React, { useCallback, useRef, useMemo } from 'react';
import {
  Box,
  VStack,
  Text,
  Flex,
  Button,
  Spinner,
  Center,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiInbox, FiRefreshCw, FiChevronDown } from 'react-icons/fi';
import LeadClaimCard from './LeadClaimCard';

// Try to import react-window for virtualization
let FixedSizeList;
try {
  const reactWindow = require('react-window');
  FixedSizeList = reactWindow.FixedSizeList;
} catch (e) {
  FixedSizeList = null;
}

const ITEM_HEIGHT = 200; // Approximate height of each card

export function LeadQueueList({
  leads = [],
  loading = false,
  hasMore = false,
  onLoadMore,
  onClaim,
  claimingLeadId,
  isBlocked = false,
  emptyMessage = 'No leads available in the queue',
}) {
  const listRef = useRef(null);
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Render individual item
  const renderItem = useCallback(({ index, style }) => {
    const lead = leads[index];
    if (!lead) return null;

    return (
      <div style={{ ...style, paddingBottom: '12px' }}>
        <LeadClaimCard
          lead={lead}
          onClaim={onClaim}
          isClaiming={claimingLeadId === lead.id}
          isBlocked={isBlocked}
        />
      </div>
    );
  }, [leads, onClaim, claimingLeadId, isBlocked]);

  // Empty state
  if (!loading && leads.length === 0) {
    return (
      <Center py={12} flexDirection="column">
        <Icon as={FiInbox} boxSize={16} color="gray.400" mb={4} />
        <Text fontSize="lg" color="gray.500" textAlign="center">
          {emptyMessage}
        </Text>
        <Text fontSize="sm" color="gray.400" mt={2}>
          Check back later for new opportunities
        </Text>
      </Center>
    );
  }

  // Loading state
  if (loading && leads.length === 0) {
    return (
      <Center py={12}>
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="3px" />
          <Text color="gray.500">Loading leads...</Text>
        </VStack>
      </Center>
    );
  }

  // Use virtualized list if available and we have many items
  if (FixedSizeList && leads.length > 20) {
    return (
      <Box>
        <FixedSizeList
          ref={listRef}
          height={600}
          width="100%"
          itemCount={leads.length}
          itemSize={ITEM_HEIGHT}
          overscanCount={3}
        >
          {renderItem}
        </FixedSizeList>
        
        {/* Load More */}
        {hasMore && (
          <Center py={4}>
            <Button
              variant="outline"
              onClick={onLoadMore}
              isLoading={loading}
              leftIcon={<FiChevronDown />}
            >
              Load More
            </Button>
          </Center>
        )}
      </Box>
    );
  }

  // Fallback to simple list
  return (
    <Box>
      <VStack spacing={3} align="stretch">
        {leads.map((lead) => (
          <LeadClaimCard
            key={lead.id}
            lead={lead}
            onClaim={onClaim}
            isClaiming={claimingLeadId === lead.id}
            isBlocked={isBlocked}
          />
        ))}
      </VStack>

      {/* Load More */}
      {hasMore && (
        <Center py={6}>
          <Button
            variant="outline"
            onClick={onLoadMore}
            isLoading={loading}
            leftIcon={<FiChevronDown />}
          >
            Load More Leads
          </Button>
        </Center>
      )}

      {/* Loading indicator for load more */}
      {loading && leads.length > 0 && (
        <Center py={4}>
          <Spinner size="sm" color="blue.500" mr={2} />
          <Text fontSize="sm" color="gray.500">Loading more...</Text>
        </Center>
      )}
    </Box>
  );
}

export default LeadQueueList;
